import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSubscription } from "@/contexts/SubscriptionContext";
import axios, { getApiUrl } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Check, Crown, Lock, BarChart3, Download, Star, Bell,
  Image, StickyNote, ShieldOff, Zap, ArrowLeft,
} from "lucide-react";

const API = `${getApiUrl()}/api`;

const FEATURE_LIST = [
  { icon: BarChart3, label: "Full Statistics", desc: "All charts, deep dives, milestones & more" },
  { icon: Star, label: "Priority Tracking", desc: "Track and organize your gaming priorities" },
  { icon: Image, label: "Signature Generator", desc: "Custom profile & game card signatures" },
  { icon: Download, label: "Export Data", desc: "Export your achievements and game data" },
  { icon: StickyNote, label: "Achievement Notes", desc: "Notes, links & checklists on individual achievements" },
  { icon: Bell, label: "Notifications", desc: "Text & push notifications (coming soon)" },
  { icon: ShieldOff, label: "Ad-Free Experience", desc: "Browse without any advertisements" },
];

export default function Upgrade() {
  const { isPro, subscription, paywallEnabled } = useSubscription();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [trialLoading, setTrialLoading] = useState(false);
  const [billingCycle, setBillingCycle] = useState("monthly");

  const handleCheckout = async (planId) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/subscription/checkout`, {
        originUrl: window.location.origin,
        planId,
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start checkout");
    } finally {
      setLoading(false);
    }
  };

  const handleStartTrial = async () => {
    setTrialLoading(true);
    try {
      const res = await axios.post(`${API}/subscription/start-trial`);
      toast.success(res.data.message);
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start trial");
    } finally {
      setTrialLoading(false);
    }
  };

  if (!paywallEnabled) {
    return (
      <div className="p-6 max-w-2xl mx-auto text-center" data-testid="upgrade-page">
        <Crown className="w-12 h-12 text-purple-400 mx-auto mb-4" />
        <h1 className="text-3xl font-bold text-white font-[Unbounded] mb-2">All Features Unlocked</h1>
        <p className="text-zinc-400 mb-6">All Pro features are currently available to everyone during the Beta period.</p>
        <Button variant="outline" onClick={() => navigate("/app/dashboard")}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
        </Button>
      </div>
    );
  }

  const isMonthly = billingCycle === "monthly";
  const planId = isMonthly ? "pro_monthly" : "pro_annual";

  return (
    <div className="p-6 max-w-3xl mx-auto" data-testid="upgrade-page">
      <div className="text-center mb-10">
        <Crown className="w-14 h-14 text-purple-400 mx-auto mb-4" />
        <h1 className="text-4xl font-bold text-white font-[Unbounded] mb-2">
          {isPro ? "You're a Pro!" : "Upgrade to Pro"}
        </h1>
        <p className="text-zinc-400 text-lg">
          {isPro
            ? "Thanks for supporting Game-o-Rang!"
            : "Unlock the full experience for less than a cup of coffee."
          }
        </p>
      </div>

      {/* Pricing Card */}
      {!isPro && (
        <div className="bg-gradient-to-b from-purple-900/30 to-zinc-900/60 border border-purple-500/30 rounded-2xl p-8 mb-10 max-w-md mx-auto">
          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-1 mb-6 bg-zinc-800/80 rounded-full p-1 max-w-xs mx-auto">
            <button
              onClick={() => setBillingCycle("monthly")}
              className={`flex-1 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                isMonthly
                  ? "bg-purple-600 text-white shadow-lg"
                  : "text-zinc-400 hover:text-white"
              }`}
              data-testid="billing-monthly-btn"
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle("annual")}
              className={`flex-1 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                !isMonthly
                  ? "bg-purple-600 text-white shadow-lg"
                  : "text-zinc-400 hover:text-white"
              }`}
              data-testid="billing-annual-btn"
            >
              Annual
            </button>
          </div>

          <div className="text-center mb-6">
            <div className="flex items-baseline justify-center gap-1 mb-1">
              <span className="text-5xl font-bold text-white">
                ${isMonthly ? "0.99" : "9.99"}
              </span>
              <span className="text-zinc-400 text-lg">
                /{isMonthly ? "month" : "year"}
              </span>
            </div>
            {!isMonthly && (
              <p className="text-emerald-400 text-sm font-medium">
                Save ~$2/year vs monthly
              </p>
            )}
            <p className="text-zinc-500 text-sm mt-1">Cancel anytime</p>
          </div>

          <div className="space-y-2 mb-8">
            {FEATURE_LIST.map((f, i) => (
              <div key={i} className="flex items-center gap-3">
                <Check className="w-4 h-4 text-purple-400 flex-shrink-0" />
                <span className="text-zinc-300 text-sm">{f.label}</span>
              </div>
            ))}
          </div>

          <div className="space-y-3">
            <Button
              onClick={handleStartTrial}
              disabled={trialLoading}
              className="w-full bg-purple-600 hover:bg-purple-500 text-white font-semibold py-3"
              data-testid="start-trial-btn"
            >
              {trialLoading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Zap className="w-4 h-4 mr-2" />
              )}
              Start 7-Day Free Trial
            </Button>
            <Button
              onClick={() => handleCheckout(planId)}
              disabled={loading}
              variant="outline"
              className="w-full border-purple-500/30 text-purple-300 hover:bg-purple-500/10"
              data-testid="checkout-btn"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Lock className="w-4 h-4 mr-2" />
              )}
              Subscribe Now — ${isMonthly ? "0.99/mo" : "9.99/yr"}
            </Button>
          </div>
        </div>
      )}

      {/* Active subscription info */}
      {isPro && subscription && (
        <div className="bg-zinc-900/60 border border-green-500/30 rounded-xl p-6 mb-10 max-w-md mx-auto text-center">
          <p className="text-green-400 font-semibold mb-1">
            {subscription.status === "trialing" ? "Free Trial Active" : "Pro Subscription Active"}
          </p>
          {subscription.planId && (
            <p className="text-zinc-400 text-sm">
              Plan: {subscription.planId === "pro_annual" ? "Annual ($9.99/yr)" : "Monthly ($0.99/mo)"}
            </p>
          )}
          {subscription.currentPeriodEnd && (
            <p className="text-zinc-500 text-sm mt-1">
              {subscription.status === "trialing" ? "Trial ends" : "Renews"}: {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
            </p>
          )}
        </div>
      )}

      {/* Feature Grid */}
      <h2 className="text-xl font-bold text-white mb-4 text-center">What's Included in Pro</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {FEATURE_LIST.map((f, i) => (
          <div
            key={i}
            className="flex items-start gap-3 p-4 rounded-lg bg-zinc-900/40 border border-zinc-800"
          >
            <div className="w-9 h-9 rounded-lg bg-purple-500/15 flex items-center justify-center flex-shrink-0">
              <f.icon className="w-4 h-4 text-purple-400" />
            </div>
            <div>
              <p className="text-white font-medium text-sm">{f.label}</p>
              <p className="text-zinc-500 text-xs mt-0.5">{f.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
